import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useGetCurrentApplicationQuery,
  useGetOpenIDConfigurationQuery
} from 'services/applicationApi';
import { WebStorageStateStore } from 'oidc-client-ts';
import { setToken } from 'slice/authSlice';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { AuthProvider } from 'react-oidc-context';
import OIDCAutoSignIn from 'components/Security/OIDCAutoSignIn';
import ErrorFallback from 'pages/template/PortalTemplate';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { cleanRelativeUrl } from 'utils/urlUtils';
import { useAuth } from 'hook/useAuth';

// OIDC callback params the provider appends to the redirect URI; stripped once the round-trip
// completes. `app` is kept but upper-cased (it selects the application).
const OIDC_CALLBACK_PARAMS = ['state', 'session_state', 'code'];
const UPPERCASE_QUERY_PARAMS = ['app'];

/**
 * Provides an OpenID Connect authentication layer for the application. It initializes
 * the OpenID Connect configuration by fetching it remotely and setting up the authentication
 * context for the application. If the application or the OpenID configuration is not secured,
 * it renders the children components directly. Otherwise, it wraps the children components
 * with an `AuthProvider` to manage the authentication flow.
 *
 * @component
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The children components to be rendered within
 * the authentication context.
 *
 * @returns {React.ReactElement} The OpenID authentication provider component which either
 * directly renders the children or wraps them with authentication context providers based
 * on the application's security configuration.
 *
 * @example
 * <OpenIDAuthProvider>
 *   <App />
 * </OpenIDAuthProvider>
 */
function OIDCAuthProvider({ children }) {
  const { formatMessage: msg } = useIntl();
  const dispatch = useDispatch();
  const { resetOIDCToken } = useAuth();

  const currentLocation =
    window.location.origin +
    cleanRelativeUrl(window.location, OIDC_CALLBACK_PARAMS, UPPERCASE_QUERY_PARAMS);

  const [openIDConfigurationInitialized, setOpenIDConfigurationInitialized] = useState(false);
  const [openIDConfiguration, setOpenIDConfiguration] = useState({
    enabled: false,
    automaticSilentRenew: true,
    redirect_uri: currentLocation,
    post_logout_redirect_uri: currentLocation,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: (user) => {
      const cleanedUrl = cleanRelativeUrl(
        window.location,
        OIDC_CALLBACK_PARAMS,
        UPPERCASE_QUERY_PARAMS
      );
      // Restore the pre-login deep link when there is a real target beyond the portal root. The
      // OIDC round-trip drops location.hash, where this HashRouter portal keeps the route and the
      // externalId. Stripping the OIDC query params already forces a real document navigation, so
      // a full replace lets the freshly mounted router read the deep target — replaceState alone
      // would not, react-router v6 listening to popstate only, never hashchange.
      const returnHash = user?.state?.returnHash;
      if (returnHash && returnHash !== '#' && returnHash !== '#/') {
        window.location.replace(window.location.origin + cleanedUrl + returnHash);
        return;
      }
      // No deep link to restore: keep the token and just strip the OIDC params from the URL.
      dispatch(setToken(user?.id_token));
      window.history.replaceState({}, document.title, cleanedUrl);
    }
  });

  const {
    data: appConf = null,
    error: appConfError = false,
    isLoading: appConfLoading = true
  } = useGetCurrentApplicationQuery();

  const { data: openIDRemoteConf, error: openIDConfError } = useGetOpenIDConfigurationQuery();

  useEffect(() => {
    if (openIDRemoteConf) {
      setOpenIDConfiguration((currentConfig) => ({
        ...currentConfig,
        ...{ enabled: openIDRemoteConf.enabled },
        ...openIDRemoteConf.parameters
      }));
      setOpenIDConfigurationInitialized(true);
    }
  }, [openIDRemoteConf]);

  if (openIDConfError) {
    const error = {
      message: msg({ id: 'error.openID.configuration' }),
      stack: openIDConfError.message ?? 'openIDConfError',
      status: openIDConfError.status
    };
    console.warn(JSON.stringify(error));
    resetOIDCToken();
    return null;
  }

  if (appConfError) {
    const error = {
      message: msg({ id: 'error.openID.connect' }),
      stack: appConfError.message
    };
    if (!openIDConfigurationInitialized && !openIDConfError) {
      return (
        <IndeterminateLoading
          sx={{ width: '100%' }}
          message={msg({ id: 'loading.configuration' })}
        />
      );
    }
    if (openIDRemoteConf?.enabled) {
      return null;
    }
    return <ErrorFallback error={error} />;
  }

  if (!openIDConfigurationInitialized || appConfLoading) {
    return (
      <IndeterminateLoading sx={{ width: '100%' }} message={msg({ id: 'loading.configuration' })} />
    );
  }

  if (openIDConfiguration?.enabled && appConf?.secured) {
    return (
      <AuthProvider {...openIDConfiguration}>
        <OIDCAutoSignIn>{children}</OIDCAutoSignIn>
      </AuthProvider>
    );
  }

  return children;
}

OIDCAuthProvider.propTypes = {
  children: PropTypes.node
};

export default OIDCAuthProvider;
